/**
 * Router Unified Address Lists Management Page
 *
 * Manages whitelist and blacklist address entries that are synchronized across all provisioned routers,
 * and provides Layer 7 firewall rule management for outbound internet service control.
 *
 * Features:
 * - Whitelist Management: Add/Edit/Remove IP addresses and DNS names with comments
 * - Blacklist View: See all blacklisted IPs across routers (read-only, auto-propagated)
 * - L7 Firewall Rules: Build and deploy MikroTik Layer 7 rules using a user-friendly service
 *   picker (WhatsApp, Microsoft 365, YouTube, etc.) or custom regex patterns
 * - Stats Panel: At-a-glance counts per status category for both lists
 * - Search & Filter: Live search + status/source-router filter on all tabs
 * - Trend Analysis: Bar chart showing blacklist detections per day to surface widespread threats
 * - Sync Status: View which routers have synced entries
 * - Manual Sync: Trigger immediate sync to all routers
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import sanitizeText from '@/utils/sanitizeText';
import { toast } from 'react-toastify';
import {
    FaPlus, FaEdit, FaTrash, FaSync, FaShieldAlt, FaBan,
    FaCheckCircle, FaTimesCircle, FaClock, FaSave, FaTimes,
    FaSearch, FaFilter, FaChartBar, FaExclamationTriangle,
    FaNetworkWired, FaLayerGroup
} from 'react-icons/fa';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell
} from 'recharts';
import RouterL7RulesTab from './RouterL7RulesTab';
import CustomAddressListsManager from '@/components/IT/CustomAddressListsManager';
import './RouterUnifiedAddressListsPage.scss';
import * as Sentry from '@sentry/react';

const RouterUnifiedAddressListsPage = () => {
    const { apiFetch } = useAuth();
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [activeTab, setActiveTab] = useState('whitelist'); // 'whitelist' | 'blacklist' | 'l7' | 'custom'

    // Whitelist state
    const [whitelistEntries, setWhitelistEntries] = useState([]);
    const [editingWhitelist, setEditingWhitelist] = useState(null);
    const [showAddWhitelist, setShowAddWhitelist] = useState(false);
    const [whitelistForm, setWhitelistForm] = useState({
        address: '',
        description: ''
    });

    // Blacklist state
    const [blacklistEntries, setBlacklistEntries] = useState([]);
    const [trendChartData, setTrendChartData] = useState([]);

    // L7 Rules state
    const [l7Rules, setL7Rules] = useState([]);
    const [l7Loading, setL7Loading] = useState(false);

    // ── Search / filter state ────────────────────────────────────────────────

    /** Free-text search applied to whitelist address + description fields. */
    const [whitelistSearch, setWhitelistSearch] = useState('');
    /** Sync-status filter for the whitelist table ('all' | 'Synced' | 'Pending' | 'Failed'). */
    const [whitelistStatusFilter, setWhitelistStatusFilter] = useState('all');

    /** Free-text search applied to blacklist IP address + source router fields. */
    const [blacklistSearch, setBlacklistSearch] = useState('');
    /** Source-router filter for the blacklist table ('all' | <routerName>). */
    const [blacklistSourceFilter, setBlacklistSourceFilter] = useState('all');
    /** Trend chart window in days (7 | 14 | 30). */
    const [trendDays, setTrendDays] = useState(14);

    // ── Load functions (defined early to avoid "accessed before declaration" errors) ────────────

    /**
     * Load whitelist entries
     */
    const loadWhitelist = useCallback(async () => {
        const response = await apiFetch('/api/RouterConfig/address-lists/whitelist');
        if (!response.ok) {
            toast.error('Failed to load whitelist');
            return;
        }
        const result = await response.json();
        if (result.success) {
            setWhitelistEntries(result.data || []);
        } else {
            toast.error(result.error || 'Failed to load whitelist');
        }
    }, [apiFetch]);

    /**
     * Load blacklist entries and detection trend data
     */
    const loadBlacklist = useCallback(async () => {
        try {
            // Load current blacklist entries (for the table)
            const blacklistResponse = await apiFetch('/api/RouterConfig/address-lists/blacklist');
            if (!blacklistResponse.ok) {
                toast.error('Failed to load blacklist');
                return;
            }
            const blacklistResult = await blacklistResponse.json();
            if (blacklistResult.success) {
                setBlacklistEntries(blacklistResult.data || []);
            } else {
                toast.error(blacklistResult.error || 'Failed to load blacklist');
            }

            // Load detection trend data (for the chart)
            const trendResponse = await apiFetch(`/api/RouterConfig/blacklist/trend?daysBack=${trendDays}`);
            if (trendResponse.ok) {
                const trendResult = await trendResponse.json();
                if (trendResult.success) {
                    // Transform API data for the chart
                    const chartData = trendResult.data.map(day => ({
                        date: day.date,
                        label: new Date(day.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
                        newIPs: day.ipsAddedCount,
                        highThreat: day.highThreatCount,
                        moderateThreat: day.moderateThreatCount,
                        normalThreat: day.normalThreatCount,
                        repeatOffenders: day.repeatOffendersCount
                    }));
                    setTrendChartData(chartData);
                }
            } else {
                console.error('Failed to load blacklist trend data');
                // Don't show error toast for trend data - it's non-critical
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Failed to load blacklist data');
        }
    }, [apiFetch, trendDays]);

    /**
     * Load L7 firewall rules
     */
    const loadL7Rules = useCallback(async () => {
        setL7Loading(true);
        try {
            const response = await apiFetch('/api/RouterConfig/l7-rules');
            if (!response.ok) {
                toast.error('Failed to load L7 rules');
                return;
            }
            const result = await response.json();
            if (result.success) {
                setL7Rules(result.data || []);
            } else {
                toast.error(result.error || 'Failed to load L7 rules');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error loading L7 rules:', error);
            toast.error('Failed to load L7 rules');
        } finally {
            setL7Loading(false);
        }
    }, [apiFetch]);

    /**
     * Load whitelist or blacklist data based on active tab
     */
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            if (activeTab === 'whitelist') {
                await loadWhitelist();
            } else if (activeTab === 'blacklist') {
                await loadBlacklist();
            } else if (activeTab === 'l7') {
                await loadL7Rules();
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error loading data:', error);
            toast.error('Failed to load address list data');
        } finally {
            setLoading(false);
        }
    }, [activeTab, loadWhitelist, loadBlacklist, loadL7Rules]);

    // ── Effects ──────────────────────────────────────────────────────────────

    
    useEffect(() => {
        // Deferred via a zero-delay timer instead of calling loadData() (which sets
        // loading state) directly and synchronously in the effect body - avoids the
        // extra synchronous render pass react-hooks/set-state-in-effect warns about.
        // Bonus: the cleanup cancels a stale load if activeTab changes again before
        // the timer fires (e.g. fast tab-switching no longer races two fetches).
        const timeoutId = setTimeout(() => {
            loadData();
        }, 0);
        return () => clearTimeout(timeoutId);
    }, [loadData]);

    // ── Auto-poll whitelist while any entries are still Pending ───────────
    useEffect(() => {
        if (activeTab !== 'whitelist') return;
        const hasPending = whitelistEntries.some(e => e.syncStatus === 'Pending');
        if (!hasPending) return;

        const interval = setInterval(async () => {
            try { await loadWhitelist(); }
            catch (catchError) {
                Sentry.captureException(catchError);
                /* swallow */
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [activeTab, whitelistEntries, loadWhitelist]);

    // ── Derived stats ────────────────────────────────────────────────────────

    /**
     * Aggregate statistics for the whitelist panel.
     * Re-computes whenever the raw entry list changes.
     */
    const whitelistStats = useMemo(() => {
        const total   = whitelistEntries.length;
        const synced  = whitelistEntries.filter(e => e.syncStatus === 'Synced').length;
        const pending = whitelistEntries.filter(e => e.syncStatus === 'Pending').length;
        const failed  = whitelistEntries.filter(e => e.syncStatus === 'Failed').length;
        return { total, synced, pending, failed };
    }, [whitelistEntries]);

    /**
     * Aggregate statistics for the blacklist panel.
     * All entries returned by the API are active (expired records are deleted server-side),
     * so we only track total and unique source routers.
     */
    const blacklistStats = useMemo(() => {
        const total         = blacklistEntries.length;
        const uniqueRouters = new Set(blacklistEntries.map(e => e.sourceRouterName || 'Unknown')).size;
        return { total, uniqueRouters };
    }, [blacklistEntries]);

    /**
     * Unique source-router names for the blacklist filter dropdown.
     */
    const blacklistSourceRouters = useMemo(() => (
        [...new Set(blacklistEntries.map(e => e.sourceRouterName || 'Unknown'))].sort()
    ), [blacklistEntries]);

    // ── Filtered lists ───────────────────────────────────────────────────────

    /** Whitelist entries filtered by current search term and sync-status selector. */
    const filteredWhitelist = useMemo(() => {
        const term = whitelistSearch.toLowerCase();
        return whitelistEntries.filter(entry => {
            const matchesSearch = !term
                || (entry.address || '').toLowerCase().includes(term)
                || (entry.description || '').toLowerCase().includes(term);
            const matchesStatus = whitelistStatusFilter === 'all'
                || entry.syncStatus === whitelistStatusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [whitelistEntries, whitelistSearch, whitelistStatusFilter]);

    /** Blacklist entries filtered by current search term and source router. */
    const filteredBlacklist = useMemo(() => {
        const term = blacklistSearch.toLowerCase();
        return blacklistEntries.filter(entry => {
            const matchesSearch = !term
                || (entry.address || '').toLowerCase().includes(term)
                || (entry.sourceRouterName || '').toLowerCase().includes(term);
            const matchesSource = blacklistSourceFilter === 'all'
                || (entry.sourceRouterName || 'Unknown') === blacklistSourceFilter;
            return matchesSearch && matchesSource;
        });
    }, [blacklistEntries, blacklistSearch, blacklistSourceFilter]);

    // ── Trend analysis ───────────────────────────────────────────────────────

    /**
     * Reload trend data when the window changes
     */
    useEffect(() => {
        if (activeTab !== 'blacklist') return;
        // Same deferred-timer fix as the loadData effect above (loadBlacklist also
        // sets state synchronously as its first action otherwise). Also debounces
        // rapid trendDays window clicks so only the last selection actually fetches.
        const timeoutId = setTimeout(() => {
            loadBlacklist();
        }, 0);
        return () => clearTimeout(timeoutId);
    }, [activeTab, trendDays, loadBlacklist]);

    /**
     * Determine whether the trend is escalating: the second half of the
     * selected window has significantly more new unique IPs than the first half.
     * Used to render a warning banner when a surge in new threats is detected.
     */
    const isTrendEscalating = useMemo(() => {
        if (trendChartData.length < 4) return false;
        const half   = Math.floor(trendChartData.length / 2);
        const first  = trendChartData.slice(0, half).reduce((s, d) => s + d.newIPs, 0);
        const second = trendChartData.slice(half).reduce((s, d) => s + d.newIPs, 0);
        // Flag if second half volume is ≥ 1.5× first half and there are meaningful numbers
        return second > 0 && second >= first * 1.5 && second >= 3;
    }, [trendChartData]);


    /**
     * Add whitelist entry
     */
    const handleAddWhitelist = async () => {
        if (!whitelistForm.address.trim()) {
            toast.warning('Please enter an IP address or DNS name');
            return;
        }

        // Close the dialog immediately so the user isn't blocked while routers sync
        setShowAddWhitelist(false);
        const formSnapshot = { ...whitelistForm };
        setWhitelistForm({ address: '', description: '' });

        try {
            const response = await apiFetch('/api/RouterConfig/address-lists/whitelist', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formSnapshot)
            });

            const result = await response.json();
            if (response.ok && result.success) {
                toast.success('Whitelist entry added — syncing to routers in background');
                await loadWhitelist();
            } else {
                toast.error(result?.error || 'Failed to add whitelist entry');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error adding whitelist entry:', error);
            toast.error('Failed to add whitelist entry');
        }
    };

    /**
     * Update whitelist entry (comment only)
     */
    const handleUpdateWhitelist = async (id) => {
        const entry = whitelistEntries.find(e => e.id === id);
        if (!entry) return;

        try {
            const response = await apiFetch(`/api/RouterConfig/address-lists/whitelist/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ description: entry.description })
            });

            const result = await response.json();
            if (response.ok && result.success) {
                toast.success('Whitelist entry updated and synced to routers');
                setEditingWhitelist(null);
                await loadWhitelist();
            } else {
                toast.error(result?.error || 'Failed to update whitelist entry');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error updating whitelist entry:', error);
            toast.error('Failed to update whitelist entry');
        }
    };

    /**
     * Delete whitelist entry
     */
    const handleDeleteWhitelist = async (id) => {
        if (!confirm('Are you sure you want to remove this whitelist entry? It will be removed from all routers.')) {
            return;
        }

        try {
            const response = await apiFetch(`/api/RouterConfig/address-lists/whitelist/${id}`, {
                method: 'DELETE'
            });

            const result = await response.json();
            if (response.ok && result.success) {
                toast.success('Whitelist entry removed from all routers');
                await loadWhitelist();
            } else {
                toast.error(result?.error || 'Failed to remove whitelist entry');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error deleting whitelist entry:', error);
            toast.error('Failed to remove whitelist entry');
        }
    };

    /**
     * Delete blacklist entry
     */
    const handleDeleteBlacklist = async (id) => {
        if (!confirm('Are you sure you want to remove this blacklist entry? It will be removed from all routers.')) {
            return;
        }

        try {
            const response = await apiFetch(`/api/RouterConfig/address-lists/blacklist/${id}`, {
                method: 'DELETE'
            });

            const result = await response.json();
            if (response.ok && result.success) {
                toast.success('Blacklist entry removed from all routers');
                await loadBlacklist();
            } else {
                toast.error(result?.error || 'Failed to remove blacklist entry');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error deleting blacklist entry:', error);
            toast.error('Failed to remove blacklist entry');
        }
    };

    /**
     * Manual sync to all routers
     */
    const handleManualSync = async () => {
        setSyncing(true);
        try {
            const response = await apiFetch('/api/RouterConfig/address-lists/sync', {
                method: 'POST'
            });

            const result = await response.json();
            if (response.ok && result.success) {
                toast.success('Address lists synced to all routers');
                await loadData();
            } else {
                toast.error(result?.error || 'Failed to sync address lists');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error syncing address lists:', error);
            toast.error('Failed to sync address lists');
        } finally {
            setSyncing(false);
        }
    };

    /**
     * Render whitelist tab content.
     * Includes a stats row, search/filter bar, and the entries table.
     */
    const renderWhitelistTab = () => (
        <div className="address-list-content">
            <div className="content-header">
                <div className="header-info">
                    <h3><FaShieldAlt /> Whitelist Management</h3>
                    <p>Manage IP addresses and DNS names that should be whitelisted across all provisioned routers</p>
                </div>
                <div className="header-actions">
                    <button
                        className="btn-sync"
                        onClick={handleManualSync}
                        disabled={syncing}
                    >
                        <FaSync className={syncing ? 'spinning' : ''} />
                        {syncing ? 'Syncing...' : 'Sync Now'}
                    </button>
                    <button
                        className="btn-primary"
                        onClick={() => setShowAddWhitelist(true)}
                    >
                        <FaPlus /> Add Entry
                    </button>
                </div>
            </div>

            {/* ── Stats chips ───────────────────────────────────────── */}
            <div className="ual-stats-row">
                <div className="ual-stat-chip ual-stat-total">
                    <span className="ual-stat-value">{whitelistStats.total}</span>
                    <span className="ual-stat-label">Total Entries</span>
                </div>
                <div className="ual-stat-chip ual-stat-success">
                    <FaCheckCircle className="ual-stat-icon" />
                    <span className="ual-stat-value">{whitelistStats.synced}</span>
                    <span className="ual-stat-label">Synced</span>
                </div>
                <div className="ual-stat-chip ual-stat-warning">
                    <FaClock className="ual-stat-icon" />
                    <span className="ual-stat-value">{whitelistStats.pending}</span>
                    <span className="ual-stat-label">Pending</span>
                </div>
                <div className="ual-stat-chip ual-stat-danger">
                    <FaTimesCircle className="ual-stat-icon" />
                    <span className="ual-stat-value">{whitelistStats.failed}</span>
                    <span className="ual-stat-label">Failed</span>
                </div>
            </div>

            {/* ── Search & filter bar ───────────────────────────────── */}
            <div className="ual-controls-bar">
                <div className="ual-search-wrap">
                    <FaSearch className="ual-search-icon" />
                    <input
                        type="text"
                        className="ual-search-input"
                        placeholder="Search by address or description…"
                        value={whitelistSearch}
                        onChange={e => setWhitelistSearch(e.target.value)}
                        aria-label="Search whitelist entries"
                    />
                    {whitelistSearch && (
                        <button
                            className="ual-search-clear"
                            onClick={() => setWhitelistSearch('')}
                            title="Clear search"
                            aria-label="Clear whitelist search"
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>
                <div className="ual-filter-wrap">
                    <FaFilter className="ual-filter-icon" />
                    <select
                        className="ual-filter-select"
                        value={whitelistStatusFilter}
                        onChange={e => setWhitelistStatusFilter(e.target.value)}
                        aria-label="Filter by sync status"
                    >
                        <option value="all">All Statuses</option>
                        <option value="Synced">Synced</option>
                        <option value="Pending">Pending</option>
                        <option value="Failed">Failed</option>
                    </select>
                </div>
                <span className="ual-result-count">
                    {filteredWhitelist.length} / {whitelistEntries.length} entries
                </span>
            </div>

            {showAddWhitelist && (
                <div className="add-entry-form">
                    <h4>Add Whitelist Entry</h4>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>IP Address or DNS Name *</label>
                            <input
                                type="text"
                                value={whitelistForm.address}
                                onChange={(e) => setWhitelistForm({ ...whitelistForm, address: e.target.value })}
                                placeholder="e.g., 192.168.1.100 or server.example.com"
                            />
                        </div>
                        <div className="form-group">
                            <label>Description / Comment</label>
                            <input
                                type="text"
                                value={whitelistForm.description}
                                onChange={(e) => setWhitelistForm({ ...whitelistForm, description: e.target.value })}
                                placeholder="e.g., Office Server"
                            />
                        </div>
                    </div>
                    <div className="form-actions">
                        <button className="btn-primary" onClick={handleAddWhitelist}>
                            <FaSave /> Add & Sync
                        </button>
                        <button
                            className="btn-secondary"
                            onClick={() => {
                                setShowAddWhitelist(false);
                                setWhitelistForm({ address: '', description: '' });
                            }}
                        >
                            <FaTimes /> Cancel
                        </button>
                    </div>
                </div>
            )}

            <div className="entries-table">
                {loading ? (
                    <div className="loading">Loading whitelist entries...</div>
                ) : whitelistEntries.length === 0 ? (
                    <div className="no-entries">
                        <FaShieldAlt />
                        <p>No whitelist entries yet</p>
                        <button className="btn-primary" onClick={() => setShowAddWhitelist(true)}>
                            <FaPlus /> Add First Entry
                        </button>
                    </div>
                ) : filteredWhitelist.length === 0 ? (
                    <div className="no-entries">
                        <FaSearch />
                        <p>No entries match your search or filter</p>
                        <button
                            className="btn-secondary"
                            onClick={() => { setWhitelistSearch(''); setWhitelistStatusFilter('all'); }}
                        >
                            <FaTimes /> Clear Filters
                        </button>
                    </div>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>Address</th>
                                <th>Description</th>
                                <th>Added</th>
                                <th>Sync Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredWhitelist.map((entry) => (
                                <tr key={entry.id}>
                                    <td>
                                        <code>{sanitizeText(entry.address)}</code>
                                    </td>
                                    <td>
                                        {editingWhitelist === entry.id ? (
                                            <input
                                                type="text"
                                                value={entry.description || ''}
                                                onChange={(e) => {
                                                    const updated = whitelistEntries.map(item =>
                                                        item.id === entry.id
                                                            ? { ...item, description: e.target.value }
                                                            : item
                                                    );
                                                    setWhitelistEntries(updated);
                                                }}
                                                className="inline-edit"
                                            />
                                        ) : (
                                            <span>{sanitizeText(entry.description || '-')}</span>
                                        )}
                                    </td>
                                    <td>
                                        {new Date(entry.createdAt).toLocaleDateString('en-GB', {
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric'
                                        })}
                                    </td>
                                    <td>
                                        <div className="sync-status">
                                            {entry.syncStatus === 'Synced' ? (
                                                <span className="status-badge success">
                                                    <FaCheckCircle /> Synced
                                                </span>
                                            ) : entry.syncStatus === 'Pending' ? (
                                                <div className="sync-progress-wrap">
                                                    <div className="sync-progress-bar">
                                                        <div
                                                            className="sync-progress-fill"
                                                            style={{ width: `${entry.totalRouters ? (entry.syncedCount / entry.totalRouters) * 100 : 0}%` }}
                                                        />
                                                    </div>
                                                    <span className="sync-progress-label">
                                                        {entry.syncedCount}/{entry.totalRouters} routers
                                                    </span>
                                                    {entry.routerSyncs && (
                                                        <div className="sync-router-details">
                                                            {entry.routerSyncs.map((rs, i) => (
                                                                <span key={i} className={`sync-router-chip ${rs.syncStatus === 'Synced' ? 'synced' : rs.syncStatus === 'Failed' ? 'failed' : 'pending'}`}>
                                                                    {rs.routerName}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="sync-progress-wrap">
                                                    <span className="status-badge error">
                                                        <FaTimesCircle /> Failed
                                                    </span>
                                                    {entry.routerSyncs && (
                                                        <div className="sync-router-details">
                                                            {entry.routerSyncs.map((rs, i) => (
                                                                <span key={i} className={`sync-router-chip ${rs.syncStatus === 'Synced' ? 'synced' : rs.syncStatus === 'Failed' ? 'failed' : 'pending'}`}>
                                                                    {rs.routerName}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            {editingWhitelist === entry.id ? (
                                                <>
                                                    <button
                                                        className="btn-icon success"
                                                        onClick={() => handleUpdateWhitelist(entry.id)}
                                                        title="Save"
                                                    >
                                                        <FaSave />
                                                    </button>
                                                    <button
                                                        className="btn-icon secondary"
                                                        onClick={() => {
                                                            setEditingWhitelist(null);
                                                            loadWhitelist();
                                                        }}
                                                        title="Cancel"
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        className="btn-icon primary"
                                                        onClick={() => setEditingWhitelist(entry.id)}
                                                        title="Edit Description"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        className="btn-icon danger"
                                                        onClick={() => handleDeleteWhitelist(entry.id)}
                                                        title="Remove"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );

    /**
     * Render blacklist tab content.
     * Includes a stats row, trend chart, search/filter bar, and the entries table.
     */
    const renderBlacklistTab = () => (
        <div className="address-list-content">
            <div className="content-header">
                <div className="header-info">
                    <h3><FaBan /> Blacklist Monitor</h3>
                    <p>
                        View IP addresses that have been blacklisted on any router
                        (automatically propagated with 24h expiration)
                    </p>
                </div>
                <div className="header-actions">
                    <button
                        className="btn-sync"
                        onClick={handleManualSync}
                        disabled={syncing}
                    >
                        <FaSync className={syncing ? 'spinning' : ''} />
                        {syncing ? 'Scanning...' : 'Scan Now'}
                    </button>
                </div>
            </div>

            {/* ── Stats chips ───────────────────────────────────────── */}
            <div className="ual-stats-row">
                <div className="ual-stat-chip ual-stat-total">
                    <span className="ual-stat-value">{blacklistStats.total}</span>
                    <span className="ual-stat-label">Total Detected</span>
                </div>
                <div className="ual-stat-chip ual-stat-info">
                    <FaNetworkWired className="ual-stat-icon" />
                    <span className="ual-stat-value">{blacklistStats.uniqueRouters}</span>
                    <span className="ual-stat-label">Source Routers</span>
                </div>
            </div>

            {/* ── Trend analysis chart ──────────────────────────────── */}
            <div className="ual-trend-section">
                <div className="ual-trend-header">
                    <div className="ual-trend-title">
                        <FaChartBar className="ual-trend-title-icon" />
                        <span>Detection Trend</span>
                        {isTrendEscalating && (
                            <span className="ual-trend-warning-badge">
                                <FaExclamationTriangle /> Escalating — surge in new threat IPs
                            </span>
                        )}
                    </div>
                    <div className="ual-trend-controls">
                        <span className="ual-trend-window-label">Window:</span>
                        {[7, 14, 30].map(days => (
                            <button
                                key={days}
                                className={`ual-trend-window-btn ${trendDays === days ? 'active' : ''}`}
                                onClick={() => setTrendDays(days)}
                            >
                                {days}d
                            </button>
                        ))}
                    </div>
                </div>
                <ResponsiveContainer width="100%" height={180}>
                    <BarChart
                        data={trendChartData}
                        margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                            width={28}
                        />
                        <Tooltip
                            formatter={(value) => [value, 'IPs Added to Blacklist']}
                            contentStyle={{
                                fontSize: '0.8rem',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px'
                            }}
                        />
                        <Bar dataKey="newIPs" name="newIPs" radius={[3, 3, 0, 0]}>
                            {trendChartData.map((entry) => (
                                <Cell
                                    key={entry.date}
                                    fill={entry.newIPs >= 10 ? '#ef4444' : entry.newIPs >= 5 ? '#f97316' : '#3b82f6'}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
                <p className="ual-trend-legend">
                    <span className="ual-legend-dot" style={{ background: '#3b82f6' }} /> Normal (&lt;5 IPs) &nbsp;
                    <span className="ual-legend-dot" style={{ background: '#f97316' }} /> Moderate (5–9 IPs) &nbsp;
                    <span className="ual-legend-dot" style={{ background: '#ef4444' }} /> High (10+ IPs)
                </p>
            </div>

            <div className="info-alert">
                <strong>Note:</strong> The detection trend chart shows daily statistics of IPs added to the blacklist across all routers.
                Active blacklist entries expire after 24 hours, but historical data is retained for trend analysis. Repeat offenders (IPs blacklisted multiple times)
                have their blacklist duration automatically escalated.
            </div>

            {/* ── Search & filter bar ───────────────────────────────── */}
            <div className="ual-controls-bar">
                <div className="ual-search-wrap">
                    <FaSearch className="ual-search-icon" />
                    <input
                        type="text"
                        className="ual-search-input"
                        placeholder="Search by IP address or source router…"
                        value={blacklistSearch}
                        onChange={e => setBlacklistSearch(e.target.value)}
                        aria-label="Search blacklist entries"
                    />
                    {blacklistSearch && (
                        <button
                            className="ual-search-clear"
                            onClick={() => setBlacklistSearch('')}
                            title="Clear search"
                            aria-label="Clear blacklist search"
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>
                {blacklistSourceRouters.length > 0 && (
                    <div className="ual-filter-wrap">
                        <FaNetworkWired className="ual-filter-icon" />
                        <select
                            className="ual-filter-select"
                            value={blacklistSourceFilter}
                            onChange={e => setBlacklistSourceFilter(e.target.value)}
                            aria-label="Filter by source router"
                        >
                            <option value="all">All Routers</option>
                            {blacklistSourceRouters.map(router => (
                                <option key={router} value={router}>{router}</option>
                            ))}
                        </select>
                    </div>
                )}
                <span className="ual-result-count">
                    {filteredBlacklist.length} / {blacklistEntries.length} entries
                </span>
            </div>

            <div className="entries-table">
                {loading ? (
                    <div className="loading">Loading blacklist entries...</div>
                ) : blacklistEntries.length === 0 ? (
                    <div className="no-entries">
                        <FaBan />
                        <p>No blacklisted IPs detected</p>
                    </div>
                ) : filteredBlacklist.length === 0 ? (
                    <div className="no-entries">
                        <FaSearch />
                        <p>No entries match your search or filter</p>
                        <button
                            className="btn-secondary"
                            onClick={() => {
                                setBlacklistSearch('');
                                setBlacklistSourceFilter('all');
                            }}
                        >
                            <FaTimes /> Clear Filters
                        </button>
                    </div>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>IP Address</th>
                                <th>First Detected</th>
                                <th>Source Router</th>
                                <th>Expires</th>
                                <th>Propagated To</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBlacklist.map((entry) => (
                                <tr key={entry.id}>
                                    <td>
                                        <code className="danger">{sanitizeText(entry.address)}</code>
                                    </td>
                                    <td>
                                        {/* Guard against null firstDetectedAt to prevent misleading epoch date (1 Jan 1970). */}
                                        {entry.firstDetectedAt
                                            ? new Date(entry.firstDetectedAt).toLocaleString('en-GB', {
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: false
                                            })
                                            : <span className="timestamp-unknown" title="Detection time not recorded">—</span>}
                                    </td>
                                    <td>
                                        {sanitizeText(entry.sourceRouterName || 'Unknown')}
                                    </td>
                                    <td>
                                        {/* Guard against null expiresAt (legacy records) to prevent misleading epoch date. */}
                                        {entry.expiresAt
                                            ? new Date(entry.expiresAt).toLocaleString('en-GB', {
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: false
                                            })
                                            : <span className="timestamp-unknown" title="Expiry time not available">—</span>}
                                    </td>
                                    <td>
                                        <div className="sync-progress-wrap">
                                            <div className="sync-progress-bar">
                                                <div
                                                    className="sync-progress-fill"
                                                    style={{ width: `${entry.totalRouters ? ((entry.propagatedToCount || 0) / entry.totalRouters) * 100 : 0}%` }}
                                                />
                                            </div>
                                            <span className="sync-progress-label">
                                                {entry.propagatedToCount || 0}/{entry.totalRouters || 0} routers
                                            </span>
                                            {entry.routerSyncs && entry.routerSyncs.length > 0 && (
                                                <div className="sync-router-details">
                                                    {entry.routerSyncs.map((rs, i) => (
                                                        <span key={i} className={`sync-router-chip ${rs.syncStatus === 'Synced' ? 'synced' : rs.syncStatus === 'Failed' ? 'failed' : 'pending'}`}>
                                                            {rs.routerName}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                className="btn-icon danger"
                                                onClick={() => handleDeleteBlacklist(entry.id)}
                                                title="Remove from all routers"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );

    return (
        <div className="router-address-lists-page">
            <div className="page-header">
                <div className="header-content">
                    <h1>Unified Address Lists</h1>
                    <p>Manage whitelist, blacklist, custom address lists, and Layer 7 firewall rules synchronized across all provisioned routers</p>
                </div>
            </div>

            {/* Tab bar — styled to match the Mikrotik config page sub-tabs pattern */}
            <div className="sub-tabs">
                <button
                    className={`sub-tab ${activeTab === 'whitelist' ? 'active' : ''}`}
                    onClick={() => setActiveTab('whitelist')}
                >
                    <FaShieldAlt /> Whitelist
                </button>
                <button
                    className={`sub-tab ${activeTab === 'blacklist' ? 'active' : ''}`}
                    onClick={() => setActiveTab('blacklist')}
                >
                    <FaBan /> Blacklist
                </button>
                <button
                    className={`sub-tab ${activeTab === 'l7' ? 'active' : ''}`}
                    onClick={() => setActiveTab('l7')}
                >
                    <FaLayerGroup /> L7 Firewall Rules
                </button>
                <button
                    className={`sub-tab ${activeTab === 'custom' ? 'active' : ''}`}
                    onClick={() => setActiveTab('custom')}
                >
                    <FaLayerGroup /> Custom Lists
                </button>
            </div>

            {activeTab === 'whitelist' && renderWhitelistTab()}
            {activeTab === 'blacklist' && renderBlacklistTab()}
            {activeTab === 'l7' && (
                <RouterL7RulesTab
                    l7Rules={l7Rules}
                    loading={l7Loading}
                    onRefresh={loadL7Rules}
                />
            )}
            {activeTab === 'custom' && <CustomAddressListsManager />}
        </div>
    );
};

export default RouterUnifiedAddressListsPage;
